import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PageShell, ShellSection } from "./PageShell";
import { StatusBadge } from "../components/common";
import { Reveal } from "../components/Reveal";
import { TURF_TALK, inquiryMailto } from "../data/content";
import { usePageMeta } from "../hooks/usePageMeta";

export default function TurfTalkPage() {
    const t = TURF_TALK;
    usePageMeta("Turf Talk — SportChant, LTD. | Titan Haven Group LLC", t.intro, {
        image: "/assets/products/turf-talk-phones.jpg",
    });
    return (
        <PageShell testId="turftalk-page" overline={t.overline} title={t.title} lede={t.intro}>
            <ShellSection>
                <Reveal className="flex flex-wrap items-center gap-3">
                    <StatusBadge testId="turftalk-status">{t.status}</StatusBadge>
                    <span className="text-xs tracking-[0.15em] uppercase text-thg-tertiary">{t.relationship}</span>
                </Reveal>
                <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 max-w-4xl" data-testid="turftalk-phone-gallery">
                    {t.phones.map((img, i) => (
                        <Reveal key={img.src} delay={0.1 + i * 0.06}>
                            <div className="rounded-[2rem] border border-thg-border bg-[#050505] p-2 shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
                                <img
                                    src={img.src}
                                    alt={img.alt}
                                    loading="lazy"
                                    className="w-full h-auto rounded-[1.6rem]"
                                />
                            </div>
                            <p className="mt-3 text-xs tracking-[0.15em] uppercase text-thg-tertiary">{img.caption}</p>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <p className="mt-6 text-xs leading-relaxed text-thg-tertiary max-w-2xl" data-testid="turftalk-disclosure">
                        {t.disclosure}
                    </p>
                </Reveal>
                <Reveal delay={0.25} className="mt-12">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-thg-tertiary">The slate, full width</p>
                    <div className="mt-4 border border-thg-border" data-testid="turftalk-gallery">
                        <img
                            src={t.galleryWide.src}
                            alt={t.galleryWide.alt}
                            loading="lazy"
                            className="w-full h-auto bg-[#0a0a0a]"
                        />
                    </div>
                </Reveal>
            </ShellSection>
            {t.sections.map((s) => (
                <ShellSection key={s.heading}>
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">{s.heading}</h2>
                        <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl">{s.text}</p>
                    </Reveal>
                </ShellSection>
            ))}
            <ShellSection>
                <Reveal className="border border-thg-border bg-thg-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                    <div className="max-w-xl">
                        <h2 className="font-display text-xl md:text-2xl font-medium text-white">
                            See Turf Talk working.
                        </h2>
                        <p className="mt-3 text-sm md:text-base leading-relaxed text-thg-secondary">
                            Access is controlled during the closed beta. Request a guided demonstration through
                            Titan Haven Group.
                        </p>
                        <p className="mt-5 text-sm text-thg-secondary" data-testid="turftalk-address">
                            Product website:{" "}
                            <a
                                href={t.productAddress.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="turftalk-product-link"
                                className="text-white underline decoration-thg-red underline-offset-4 hover:text-thg-secondary transition-colors duration-300"
                            >
                                {t.productAddress.label} — {t.productAddress.domain}
                            </a>
                            <span className="block mt-1 text-xs text-thg-tertiary">
                                The public product front door — not open access.
                            </span>
                        </p>
                    </div>
                    <a
                        href={inquiryMailto(t.cta.subject)}
                        data-testid="turftalk-demo-cta"
                        className="inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase shrink-0"
                    >
                        {t.cta.label} <ArrowUpRight size={16} />
                    </a>
                </Reveal>
                <Reveal delay={0.1} className="mt-8 flex flex-wrap items-center gap-6">
                    <Link
                        to="/portfolio/media"
                        data-testid="turftalk-back-media"
                        className="inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                    >
                        <ArrowLeft size={15} /> Sports / Media / IP
                    </Link>
                    <Link
                        to="/portfolio"
                        data-testid="turftalk-back-portfolio"
                        className="inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                    >
                        <ArrowLeft size={15} /> Back to portfolio
                    </Link>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
}
